// attack 뷰, rally 편집기 하나: 조립과 시간 손잡이와 내보내기와 내보낸 뒤의 조작
//
// ── 왜 rally 탭과 attack 탭이 하나가 됐는가 (2026-08-27) ──────────────────
//
// 전에는 탭이 둘이었다. rally 탭이 폼과 미니 시퀀스와 「조립 마치기」를 들고, attack 탭이
// 카드 목록과 time handle 과 confirm 과 내보낸 뒤의 다시 잡기를 들었다. 그래서 두 탭이 서로를
// 「저쪽 탭에서」로 가리켰다. 조립을 마치면 「attack 탭에 time handle 이 나타났습니다」, 접으면
// 「rally 탭에서 새로 짭니다」, counter rally 를 붙이면 「줄 전체는 attack 탭에 있습니다」.
// 2026-08-19 사용자 피드백이 그것을 결함으로 지적했고, 2026-08-27 사용자가 attack 이름으로
// 합치기로 정했다.
//
// 합치면서 목록도 하나가 됐다. **같은 시퀀스의 목록이 둘이면 서로 다르게 낡는다**는 것이
// 이 저장소가 치른 대가다. 미니 시퀀스와 카드 목록이 한 시퀀스를 각자 그리는 동안 좁은
// 폭에서 서로 다르게 무너졌고, 한쪽을 고쳐도 다른 쪽은 그대로였다. 이제 목록은
// `rally/mini-sequence.tsx` 하나이고 정렬 토글과 내보낸 뒤의 「고치기」 버튼이 거기 산다.
//
// ── 문맥 셋이 한 자리에서 갈린다 ────────────────────────────────────────────
//
// **문맥 셋이 서로 다르다. 그것 자체가 「확정과 미확정」의 구분이다** (2026-08-13 사용자
// 결정). 구성 화면 본문은 절대 시각을 한 글자도 쓸 수 없어서 「확정된 시각」을 시각으로
// 보여 줄 수 없다. 그래서 문맥으로 말한다.
//
//   조립 중    좌열 폼과 우열 목록. 목록 아래에 「조립 마치기」. 손잡이도 confirm 도 **아예 없다.**
//   마친 뒤    같은 2열 위에 손잡이가 나타난다. 「아직 확정되지 않았습니다」를 함께 적는다.
//   내보낸 뒤  「confirm 됨, 시각 확정」 배지가 붙고 손잡이와 confirm 이 사라진다. 좌열은
//              counter rally 폼이 되고, 목록 아래에 다시 잡기와 줄 수정과 떠나는 두 길이 선다.
//
// 서버는 세 상태 모두에서 줄 추가를 받는다(내보낸 뒤는 counter rally). 줄 빼기만 내보낸
// 뒤에 거절하고, 그래서 목록의 빼기 버튼이 그때 DOM 에서 빠진다.
//
// 없는 것을 숨김이나 비활성으로 두지 않고 **DOM 에서 뺀다.** 「노출되지 않는다」를 숨김으로
// 만족시키면 그 판정이 「어느 속성을 보느냐」에 달리게 되고, 달린 판정은 채점자의 재량이 된다.
//
// **금지된 것은 rally 수명주기 문구(`SET`, `MARCH`, `HIT`)와 「발사가능」이지 dispatch
// 여부가 아니다.** 그래서 이 배지는 계약에 걸리지 않는다. 다만 그 구분이 사람의 읽기에
// 달려 있으므로, 배지가 떠 있는 상태에서 delta 전용 검사가 초록인 것을 근거로 남겼다.
//
// ── 좌열 폼과 우열 목록은 같은 상태 위에 있다 ──────────────────────────────
//
// 둘이 각자 서버를 읽으면 적재 직후의 한 순간에 좌열은 새 줄을 알고 우열은 모르는 상태가
// 생기고, 그 어긋남은 화면에서 「적재가 안 됐다」로 보인다. 그래서 `useSequence` 는 이
// 컴포넌트가 한 번만 부르고 둘에게 내려보낸다.
//
// ── 340픽셀에서 2열이 성립하는가 ───────────────────────────────────────────
//
// `md:` 브레이크포인트(768px) 위에서만 2열이고 그 아래에서는 한 열로 접힌다. 정본이 계약한
// 최소 폭 340 에서는 접힌 모양이 답이고, 브라우저 검증이 그 폭에서 본문이 가로로 넘치지
// 않는 것을 잰다.
//
// ── 손잡이는 프리셋 둘과 직접 입력을 함께 둔다 (2026-08-13 사용자 결정) ────
//
// 실전에서는 클릭 한 번으로 끝나는 것이 중요하고(재구성 스텝의 30초 원클릭이 같은 문법을
// 쓴다), 그 밖의 값이 필요할 때가 있다. **하한 차단은 직접 입력에만 걸린다**, 프리셋 둘
// 다 6초를 넘기 때문이다. 판정은 서버가 지고 화면은 그 응답을 그대로 보인다. 화면이 자기
// 판정을 한 벌 더 가지면 둘이 서로 다르게 낡는다.

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'

import type { Roster, Sequence } from '../../../../contract/schema.ts'
import {
  common as commonStrings,
  type CommonStrings,
  compose as composeStrings,
  type ComposeStrings,
} from '../../../../i18n/index.ts'
import { CT_TOKEN_KEY, readToken } from '../../../access/client.ts'
import { useLang } from '../../../shell/use-lang.ts'
import { useShellClock } from '../../../shell/use-shell-clock.ts'
import { formatTimeText, parseTimeText, TimeInput, timeParseMessage } from '../../../ui/time-input.tsx'
import { RallyFormAttack, type RallyFormAttackProps } from '../rally/form-attack.tsx'
import { RallyMiniSequence, type SortAxis } from '../rally/mini-sequence.tsx'
import {
  closeEdit,
  confirmPending,
  EDIT_IDLE,
  type EditState,
  keepEditing,
  requestEdit,
} from './edit-switch.ts'
import { RallyEditInline } from './rally-edit.tsx'
import { Icon, IconTile } from '../../../ui/icons.tsx'
import { LabelHint } from '../../../ui/label-hint.tsx'
import { constantOf, useSequence } from './use-sequence.ts'

/** 지금 고른 언어의 문자열 한 벌. **모듈에 굳히지 않는다**, 굳히면 언어를 바꿔도 안 따라온다. */
function useStrings(): ComposeStrings {
  return composeStrings(useLang().lang)
}

/** 지금 고른 언어의 공용 문자열 한 벌. **모듈에 굳히지 않는다**, 굳히면 언어를 바꿔도 안 따라온다. */
function useCommon(): CommonStrings {
  return commonStrings(useLang().lang)
}

/** 문장 안의 `{자리}` 를 채운다. 번역이 붙어 어순이 달라져도 견디게 하는 최소한이다. */
function fill(template: string, values: Record<string, string>): string {
  return Object.entries(values).reduce(
    (text, [key, value]) => text.replaceAll(`{${key}}`, value),
    template,
  )
}

/**
 * 다음 할 일 하나.
 *
 * **여럿을 늘어놓지 않는다.** 처음 여는 CT 가 화면에서 찾아야 하는 것은 「지금 무엇을
 * 하면 되는가」 하나이고, 둘 이상을 같은 무게로 적으면 고르는 일이 하나 더 생긴다.
 * 이 화면에 정확히 하나만 뜬다. 탭이 둘이던 시절에는 탭마다 하나씩 있어 CT 가 두 문장을
 * 읽었고, 그 둘이 서로 다른 탭을 가리켰다.
 */
export function NextStep(props: { text: string }): React.ReactElement {
  const t = useStrings()

  return (
    <p
      data-testid="next-step"
      // **CT 가 이 화면에서 읽어야 하는 한 줄이다** (2026-08-18 타입 스케일 축).
      // 실측에서 이것이 12px 이고 상단 벽시계가 30px 였다. 화면이 자기가 무엇을 돕는
      // 도구인지를 크기로 말하지 않고 있었다.
      className="note note-accent mt-group text-sm"
    >
      <span className="font-semibold text-accent">{t.nextStepLabel}</span> / {props.text}
    </p>
  )
}

/** 프리셋 둘. 정본의 미리보기 그림이 `[ in 30s ] [ in 60s ]` 로 적은 값 그대로다. */
const HANDLE_PRESETS = [30_000, 60_000] as const

/**
 * retime 빠른 경로의 고정값. **정본이 30초로 못박았고 3초 예산이 이 경로에만 걸린다.**
 *
 * 직접 설정은 값을 치는 데 사람의 시간이 드니까 같은 표에 섞지 않는다. 섞으면 그 숫자에
 * 사람이 타이핑한 시간이 들어가 예산과 무관한 값이 된다.
 */
const RETIME_QUICK_MS = 30_000

/**
 * 내보낸 뒤에만 시계를 읽는 자리. **이 화면에서 시계를 여는 곳은 여기 하나다.**
 *
 * ── 왜 여기서 처음으로 시계가 필요한가 ─────────────────────────────────────
 *
 * 구성 화면은 시계를 한 번도 읽지 않는 것이 T5 가 세운 뼈대였고, 그것이 가능했던 이유는
 * 조립과 편집 문맥에 **시각이 아예 없기** 때문이다. 개시 Δ 는 앵커 상수의 차이이고 착탄 Δ 는
 * offset 그 자체라 둘 다 시각과 무관하다.
 *
 * 진행 중 시퀀스는 다르다. 착탄 원점이 절대 시각으로 얼어붙어 있고 하한이 `지금 + 6초` 라,
 * **어느 옵션이 불가인지가 지금 몇 시인가에 달린다.** 정본이 「미리보기가 불가를 보여 주고
 * confirm 자체가 되지 않는다」를 계약했으므로 화면이 그 판정을 미리 해야 하고, 그러려면
 * 지금을 알아야 한다. 「이미 개시한 줄 수」도 같은 지금을 쓴다.
 *
 * **그래도 delta 전용 계약은 깨지지 않는다.** 읽은 시각으로 그리는 값이 「개시 여유」와
 * 「최소 착탄 간격」이고 둘 다 기간이지 시각이 아니다. 절대 시각은 여전히 이 화면 어디에도
 * 없고, 자동 검사가 dispatch 전과 후 양쪽에서 그것을 잰다.
 *
 * ── 왜 render prop 인가 ────────────────────────────────────────────────────
 *
 * 훅은 조건부로 부를 수 없으므로 `AttackView` 안에서 부르면 조립 중에도 시계 동기가 돌고
 * 5초마다 서버를 두드린다. 그래서 내보낸 뒤에만 마운트되는 자리로 밀어야 한다. 그런데 그
 * 지금을 쓰는 곳이 둘이다. 좌열의 counter rally 폼과 목록 아래의 다시 잡기와 줄 수정이고,
 * 둘 사이에 우열 목록이 끼어 있어 한 컴포넌트로 감쌀 수 없다. 탭이 둘이던 시절에는 둘이
 * 각자 시계를 열어도 한 번에 하나만 떠 있어 문제가 없었는데, 한 화면이 되면서 두 벌이
 * 동시에 돌게 됐다. `useShellClock` 은 인스턴스마다 자기 `ClockSync` 와 5초 프로브를
 * 갖는다. 그래서 지금을 한 번 읽어 자식 함수에 넘긴다. **판정의 정본은 여전히 서버다**,
 * 수렴 전에는 이 시계가 기기 시계로 되돌아가므로 화면이 가능하다고 본 조합을 서버가
 * 거절할 수 있고 그때는 서버의 문장을 그대로 보인다.
 */
function DispatchedClock(props: {
  children: (nowMs: number) => React.ReactElement
}): React.ReactElement {
  // 하한이 6초라 0.5초 칸이면 경계에서 최대 한 칸 늦게 반응한다. 더 잘게 그려도 판정은
  // 서버가 지므로 얻는 것이 없고, 대신 매 프레임 다시 그리는 값을 만든다.
  const clock = useShellClock({ tickMs: 500 })
  return props.children(clock.utcMillis)
}

/**
 * counter rally 를 붙이는 자리. 좌열의 폼이 진행 중 문맥으로 서는 것이다.
 *
 * 2026-08-15 에 잠금이 풀렸다. 재구성 스텝이 진행 중 시퀀스에 줄을 더하는 문을 열었고,
 * 그 자리가 counter rally 다. 이 폼이 옵션마다 **개시 여유와 최소 착탄 간격**을 보여 주고,
 * 바로 오른쪽 목록에 붙인 줄이 그대로 쌓인다.
 */
function CounterRallyPane(props: {
  allies: Roster[]
  sequence: Sequence
  nowMs: number
  onAdd: (input: Parameters<RallyFormAttackProps['onAdd']>[0]) => Promise<string | null>
}): React.ReactElement {
  return (
    <RallyFormAttack
      allies={props.allies}
      sequence={props.sequence}
      onAdd={props.onAdd}
      live={{ impactMainAt: props.sequence.impactMainAt ?? 0, nowMs: props.nowMs }}
    />
  )
}

/*
 * 내보낸 작전의 한 줄을 고치는 영역은 `rally-edit.tsx` 가 진다. 2026-10-14 사용자 지시로 목록
 * 전체 아래 한 자리에서 그 rally 행 바로 아래로 옮겼고, 그때 이 파일에서 나갔다. 어느 줄을
 * 고치는 중인지와 저장 안 한 입력을 지키는 규칙은 `edit-switch.ts` 에 있다.
 */

/**
 * 이미 개시한 줄 수. **되돌리기가 무엇을 앗아 가는지를 숫자로 만드는 자리다.**
 *
 * `open_i = impact_main + r_i` 이고 `r_i` 는 타이밍 코어의 앵커 상수다. 계산을 여기서 다시
 * 짜지 않고 `constantOf` 를 부르는 이유는 늘 같다. 같은 식이 두 곳에 살면 둘이 서로 다르게
 * 낡고, 그 어긋남은 그럴듯한 숫자로 뜬다.
 *
 * **조립과 편집 문맥에서는 0 이다.** 거기에는 원점이 없어 「개시했다」를 판정할 근거 자체가
 * 없다. 없는 판정을 0 으로 적는 것이 아니라, 그 문맥에서는 이 컴포넌트가 아예 안 뜬다.
 */
function openedCount(sequence: Sequence, nowMs: number): number {
  const anchor = sequence.impactMainAt
  if (anchor === null) return 0
  return sequence.rallies.filter((line) => anchor + constantOf(line) <= nowMs).length
}

/**
 * 내보낸 작전을 **떠나는 두 길**, 수정과 접기.
 *
 * 둘을 나란히 두는 이유는 그 둘이 같은 물음의 두 답이기 때문이다. "이 작전을 이대로 둘 수
 * 없다" 에 대해 **수정은 편성을 지키고 접기는 편성을 버린다.** 접기만 있던 동안 줄을 빼거나
 * 타입을 바꾸려는 CT 에게 열려 있던 길이 그것 하나뿐이었고, 서버의 거절 문장이 실제로
 * 접기를 가리켰다. 그래서 20명 배정을 처음부터 다시 넣는 일이 생겼다. (2026-08-17.)
 *
 * **무장은 하나뿐이다.** 두 버튼이 각자 무장 상태를 들면 붉은 경고 둘이 한 화면에 서고, 그때
 * CT 가 읽는 문장과 누를 버튼이 갈린다. 값 하나로 두면 그 상태가 만들어질 수 없다.
 *
 * ── 확정은 방금 누른 자리에 두지 않는다 (2026-08-17 대리 관측) ─────────────
 *
 * 처음에는 무장한 버튼이 **제자리에서 확정 버튼이 됐다.** 접기와 같은 문법이라 그렇게
 * 세웠는데, 관측자가 그 자리를 250밀리초 간격으로 두 번 눌러 **rally 다섯 줄을 지웠다.**
 * 250밀리초는 사람이 실제로 두 번 탭 하는 간격이다. 경고 문장이 뜨기는 하지만 읽을 시간이
 * 없고, 접힌 편성은 되돌릴 수 없다.
 *
 * **더 나쁜 것은 이 배치를 이번 회차가 만들었다는 것이다.** 접기의 두 번 탭 성질은 전부터
 * 있었지만 그때 접기 옆에는 아무것도 없었다. 편성을 지키러 온 사람의 손가락 8픽셀 옆에
 * 편성을 버리는 버튼을 세운 것이 이 회차다.
 *
 * 그래서 무장하면 셋이 함께 일어난다. **누른 버튼이 제자리에서 「취소」가 되고**, 확정은
 * 경고 아래 **다른 자리에 다른 글자로** 서고, 반대쪽 버튼은 사라진다. 같은 자리를 두 번
 * 누르면 무장했다가 풀릴 뿐이라 **잃을 것이 없다.** 시간 지연으로 두 번째 누름을 무시하는
 * 방법도 있었지만 그쪽은 규율이다. 되돌릴 수 없는 것을 손가락이 있는 자리에서 치우는 편이
 * 오래간다. 「생기면 고친다」가 아니라 「생길 수 없게」다.
 *
 * 반대쪽 버튼까지 감추는 이유는 겨냥 실수를 아예 없애기 위해서다. 쉴 때 두 버튼은 글자
 * 마지막 두 자와 색조 하나로만 갈리는데(관측자가 두 색의 차이가 파랑 채널 하나뿐임을
 * 쟀다), 무장한 화면에서는 화면이 한 가지만 말한다.
 *
 * **시계를 이 컴포넌트가 열지 않는다.** 「이미 개시한 줄 수」에 지금이 필요한데, 시계는 위의
 * `DispatchedClock` 하나가 열고 여기까지 내려온다.
 */
function LeaveDispatched(props: {
  sequence: Sequence
  /** 위에서 받은 지금. **이 컴포넌트가 시계를 열지 않는 이유는 주석에 있다.** */
  nowMs: number
  /** 열린 줄 수정에 저장 안 한 입력이 있는가. 있으면 두 경고가 그것도 사라진다고 말한다. */
  unsavedEdit: boolean
  onReopen: () => Promise<string | null>
  onDiscard: () => Promise<string | null>
}): React.ReactElement {
  const t = useStrings()
  const [armed, setArmed] = useState<'reopen' | 'discard' | null>(null)

  const lines = props.sequence.rallies.length
  const opened = openedCount(props.sequence, props.nowMs)

  /** 확정은 경고 아래의 다른 버튼이 진다. 이 함수는 그 버튼만 부른다. */
  const commit = (which: 'reopen' | 'discard'): void => {
    // 성공하면 이 컴포넌트가 통째로 사라지므로 무장을 풀 자리가 없다. 실패했을 때만
    // 풀어야 하는데, 그때 무장이 남아 있으면 확정 버튼을 한 번 더 눌러 재시도할 수 있다.
    void (which === 'reopen' ? props.onReopen() : props.onDiscard()).then((failed) => {
      if (failed !== null) setArmed(null)
    })
  }

  return (
    <div className="card-rule mt-block pt-group">
      <div className="flex flex-wrap items-center gap-2">
        {/* **무장하면 그 자리가 취소가 된다.** 같은 자리를 두 번 누르는 손이 확정에 닿지
            않게 하는 것이 이 배치의 전부다. 반대쪽 버튼은 사라져 겨냥 실수도 없앤다. */}
        {armed !== 'discard' && (
          <button
            type="button"
            data-testid={armed === 'reopen' ? 'sequence-reopen-cancel' : 'sequence-reopen'}
            className={
              armed === 'reopen'
                ? 'btn btn-ghost'
                : 'btn btn-secondary'
            }
            onClick={() => {
              setArmed(armed === 'reopen' ? null : 'reopen')
            }}
          >
            {armed === 'reopen' ? t.reopenCancel : t.reopen}
          </button>
        )}

        {armed !== 'reopen' && (
          <button
            type="button"
            data-testid={armed === 'discard' ? 'sequence-discard-cancel' : 'sequence-discard'}
            // 쉴 때도 **채움으로** 갈린다. 글자색만 다르면 두 색의 차이가 파랑 채널
            // 하나뿐이라 나란히 두면 눈으로 못 가른다. 관측자가 그것을 쟀다. 색조가 아닌
            // 성질로 가르면 색을 못 가르는 사람에게도 남는다.
            className={
              armed === 'discard'
                ? 'btn btn-ghost'
                : 'btn btn-danger'
            }
            onClick={() => {
              setArmed(armed === 'discard' ? null : 'discard')
            }}
          >
            {armed === 'discard' ? t.discardCancel : t.discard}
          </button>
        )}
      </div>

      {/* 무장하기 **전에** 둘의 차이를 말한다. 무장한 뒤에만 말하면 어느 쪽을 누를지
          고르는 순간에는 아무 설명도 없다. 고르는 일이 곧 이 자리의 어려움이다. */}
      {armed === null && (
        <p data-testid="leave-dispatched-why" className="mt-group text-xs text-ink-mute">
          {t.reopenWhy}
        </p>
      )}

      {/* **무장한 상태의 경고는 상태에서 파생된다.** 무장이 풀리는 순간 함께 사라진다.
          저장해 두면 취소한 뒤에도 경고만 살아남는다. */}
      {armed === 'reopen' && (
        <p data-testid="sequence-reopen-warning" role="alert" className="mt-line text-xs text-accent">
          {fill(t.reopenConfirm, { n: String(lines) })}{' '}
          {/* **개시한 줄 수는 지금에 따라 달라진다.** 0 과 N 을 같은 문장으로 적으면
              「0 줄이 있습니다」가 되고, 그 문장은 참이지만 사람이 위험으로 읽는다. */}
          <span data-testid="sequence-reopen-opened" data-opened={String(opened)}>
            {opened === 0 ? t.reopenOpenedNone : fill(t.reopenOpenedSome, { n: String(opened) })}
          </span>
          {props.unsavedEdit && (
            <span data-testid="leave-drops-edit"> {t.leaveDropsEdit}</span>
          )}
        </p>
      )}

      {armed === 'discard' && (
        <p data-testid="sequence-discard-warning" role="alert" className="mt-line text-xs text-red-300">
          {fill(t.discardConfirm, { n: String(lines) })}
          {props.unsavedEdit && (
            <span data-testid="leave-drops-edit"> {t.leaveDropsEdit}</span>
          )}
        </p>
      )}

      {/* ── 확정, **경고 아래, 방금 누른 자리가 아닌 곳** ──────────────────
          글자도 「이 작전 …」으로 시작하지 않는다. 무엇을 하는지를 그 자리에서 말하는
          버튼이라, 경고를 안 읽고 눌러도 마지막 한 번은 자기가 무엇을 누르는지 읽는다. */}
      {armed !== null && (
        <button
          type="button"
          data-testid={armed === 'reopen' ? 'sequence-reopen-confirm' : 'sequence-discard-confirm'}
          className={`btn mt-group ${armed === 'reopen' ? 'btn-primary' : 'btn-danger-armed'}`}
          onClick={() => {
            commit(armed)
          }}
        >
          {armed === 'reopen' ? t.reopenGo : t.discardGo}
        </button>
      )}
    </div>
  )
}

/**
 * 내보낸 뒤 목록 아래에 서는 조작들. 다시 잡기와 떠나는 두 길. 줄 수정은 2026-10-14 에 목록의
 * 그 행 아래로 옮겨 갔다(`rally-edit.tsx`).
 *
 * 시계는 열지 않는다. 지금은 `DispatchedClock` 이 한 번 읽어 내려보낸다. 여기서 하나를 더
 * 열면 내보낸 상태에서 시계 동기가 두 벌 돌고, 그 왕복은 화면 어디에도 안 보인다.
 * **그려지는 값은 여전히 전부 기간이다**, 개시 여유와 최소 착탄 간격이고 절대 시각이
 * 아니다. 자동 검사가 dispatch 전과 후 양쪽에서 그것을 잰다.
 */
function DispatchedTools(props: {
  sequence: Sequence
  nowMs: number
  /**
   * 알림 한 줄(다시 잡았다, 고쳤다). **부모가 쥔다.** 줄 수정이 목록 안으로 옮겨 가면서
   * 「고쳤습니다」를 내는 쪽이 이 컴포넌트 밖이 됐는데, 알림 자리는 그대로 여기다.
   */
  notice: string
  onNotice: (text: string) => void
  /** 열린 줄 수정에 저장 안 한 입력이 있는가. 되돌리기와 접기의 경고가 그 사실도 말한다. */
  unsavedEdit: boolean
  onRetime: (handleMs: number) => Promise<string | null>
  onReopen: () => Promise<string | null>
  onDiscard: () => Promise<string | null>
}): React.ReactElement {
  const t = useStrings()
  const c = useCommon()
  const [text, setText] = useState('')
  const [message, setMessage] = useState('')
  const { notice, onNotice: setNotice } = props
  /**
   * 다시 잡기가 **몇 번 돌았는가.** 화면이 세는 값이지 화면이 쓰는 값이 아니다.
   *
   * 왜 필요한가. 다시 잡기의 성질이 「편성이 한 글자도 안 바뀐다」라서, 성공한 회차와 아무
   * 일도 없는 회차가 화면에서 글자 하나 다르지 않다. 알림 문구도 매번 같은 문장이다. 그래서
   * **끝났다는 사실 자체를 셀 수 있게** 한 칸을 둔다. 3초 예산을 재는 검사가 클릭에서
   * 완료까지를 잡으려면 「완료」에 해당하는 관측 가능한 순간이 있어야 하고, 그것이 없으면
   * 재는 배선이 왕복만 재고 화면이 다시 그려지는 시간을 빠뜨린다.
   *
   * 열람 화면이 신호 재예약 횟수를 같은 이유로 화면에 세고 있다.
   */
  const [rounds, setRounds] = useState(0)

  /**
   * 다시 잡기 바의 실제 높이를 `--retime-bar-h` 로 내놓는다. 목록 안 줄 수정 영역의 저장과
   * 취소 줄이 이 값만큼 바닥에서 띄워 끈적인다. 360 에서 바가 약 265픽셀이고 편집 영역이
   * 약 700픽셀이라, 굴림만으로는 머리와 버튼을 함께 바 위에 세울 수 없었다(2026-10-14 수정 후
   * 촬영의 계측). 바를 접거나 숨기지 않고 버튼 쪽을 바 위로 올린다. 열람 화면의 `--dock-h` 와 같은 방식이다.
   */
  const barRef = useRef<HTMLDivElement>(null)
  useLayoutEffect(() => {
    const bar = barRef.current
    if (bar === null) return
    const measure = (): void => {
      document.documentElement.style.setProperty(
        '--retime-bar-h',
        `${String(bar.getBoundingClientRect().height)}px`,
      )
    }
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(bar)
    return () => {
      observer.disconnect()
      document.documentElement.style.setProperty('--retime-bar-h', '0px')
    }
  }, [])

  const retime = async (handleMs: number, typedWhenSent: string): Promise<void> => {
    const failed = await props.onRetime(handleMs)
    setMessage(failed ?? '')
    setNotice(failed === null ? t.retimed : '')
    if (failed === null) setRounds((n) => n + 1)
    // 적용됐으면 칸을 비운다. **다만 그 사이에 사람이 친 글자는 지우지 않는다.** 왕복이
    // 도는 동안 다음 값을 치기 시작한 CT 의 타이핑을 뒤늦게 도착한 비우기가 덮는다.
    // 명부와 손잡이가 같은 경합을 이미 만났다.
    if (failed === null) setText((current) => (current === typedWhenSent ? '' : current))
  }

  /** 지금 칸에 든 값. 비어 있거나 형식이 아니면 원클릭이 놓을 값이 다음 값이다. */
  const pending = parseTimeText(text)

  const applyTyped = async (): Promise<void> => {
    const parsed = parseTimeText(text)
    if (!parsed.ok) {
      setMessage(timeParseMessage(parsed.reason, c))
      return
    }
    // **화면이 하한을 스스로 판정하지 않는다.** 서버가 이미 가르고 있고, 두 벌이면 둘이
    // 서로 다르게 낡는다. 경계값 6초가 허용되는 것도 그쪽 한 곳에서만 정해진다.
    await retime(parsed.millis, text)
  }

  return (
    <>
      {/* ── 하단 고정 바, **retime 을 접지 않고 세운다** ────────────────────
          정본이 이것을 중요 기능으로 못박고 "화면 설계가 이 조작을 축소하거나 숨기지
          않는다" 까지 계약했다. 그래서 내보내기가 있던 자리를 그대로 물려받는다.
          접힌 곳도, 더 눌러야 나오는 곳도 아니다. */}
      <div
        ref={barRef}
        data-testid="retime-bar"
        data-retime-count={String(rounds)}
        className="dock-bar sticky bottom-0 mt-block flex flex-wrap items-end gap-2 py-group"
      >
        {/* 좁은 폭(640 미만)에서는 이 글 칸이 한 줄을 다 쓰고 30초 버튼이 그 아래 줄로 내려간다. 전에는 글 칸의 기본 폭이 0 이라 버튼
            옆에 남는 100픽셀 남짓에 안내 넷이 세로로 쌓였고, 360 에서 바 높이가 KO 372, EN 548 픽셀(화면 740 의 절반 넘게)이 되어 목록의
            줄 수정 영역을 거의 다 덮었다(2026-10-14 세션 실측). */}
        <div className="min-w-0 flex-1 basis-full sm:basis-0">
          {/* c2 장식: 다시 잡기 바도 아이콘 타일 + 제목으로 시작한다(time handle 바와 같은 문법). */}
          <p className="section-title flex items-center gap-2">
            <IconTile name="retime" tone="accent" size="sm" />
            {/* 라벨(다시 잡기)과 짧은 뜻풀이(편성은 그대로) 두 요소. 같은 줄 곁에 서서 고정 바의 키를 늘리지 않는다(2026-10-14 사용자 지시). */}
            <LabelHint label={t.retimeTitle} hint={t.retimeTitleHint} />
          </p>
          <div className="mt-line flex flex-wrap items-end gap-2">
            <div className="w-32">
              <TimeInput
                testId="retime-input"
                label={t.retimeCustom}
                value={text}
                onChange={(next) => {
                  setText(next)
                  setMessage('')
                }}
                compact
              />
            </div>
            <button
              type="button"
              data-testid="retime-apply"
              className="btn btn-secondary"
              onClick={() => void applyTyped()}
            >
              {t.retimeApply}
            </button>
          </div>
          {/* ── 두 줄이 서로 다른 것을 말한다 ──────────────────────────────
              **앞 줄은 「다음에 일어날 일」, 뒷줄은 「걸려 있는 값」이다.**

              한 줄만 두었더니 그 줄이 마지막으로 적용한 값을 말하는데 바로 아래 버튼은
              30초라고 적혀 있어, 같은 자리에서 두 숫자가 다른 말을 했다. 실패 직후
              1~2초를 다투는 사람이 읽는 것이 버튼 위의 한 줄인데 그 줄이 누를 대상과
              다른 것을 가리킨 것이다. (2026-08-15 대리 관측이 D1 에서 1점으로 값 매겼다.)

              **앞 줄은 칸을 따라간다**, 칸이 비어 있으면 원클릭이 놓을 값을, 값이 들어
              있으면 그 값을 미리 말한다. 그래서 무엇을 누르든 그 줄이 언제나 참이다. */}
          <p data-testid="retime-will-apply" className="mt-line text-xs">
            {fill(t.retimeWillApply, {
              value: formatTimeText(pending.ok ? pending.millis : RETIME_QUICK_MS),
            })}
          </p>
          {/* ── 뒷줄은 상태가 아니라 기록이다 (2026-08-31 사용자 결정) ─────────
              **`handleMs` 는 시간이 흘러도 안 움직이는 상수다.** 뜻은 「다시 잡은 그
              순간부터 얼마 뒤에 가장 이른 개시를 놓았는가」이고, 그 「그 순간」은 이미
              지나갔다. 그런데 문장은 「지금은 지금부터 {value} 뒤에 잡혀 있습니다」로
              **매 순간의 남은 시간을 주장**하고 있었다. 그래서 적용 직후 1초만 지나도
              거짓이 되고, 개시가 지난 뒤에는 바로 아래 되돌리기 경고가 「이미 개시한
              rally 가 2 줄 있습니다」라고 정확히 세는 동안 이 줄만 미래를 가리켰다.

              **틀린 문장의 출처는 구현이 아니라 수정안이다.** 2026-08-15 대리 채점이
              「마지막 적용값을 보여주고 싶다면 문법을 과거로 바꾼다」고 적으면서 예시
              문장만 현재형으로 달았고, 구현이 그 예시를 글자 그대로 옮겼다. 그 뒤
              2026-08-16 T8 2회차가 D1 에서, 2026-08-27 T5 개정이 A3 에서 같은 자리를 각각
              1점 깎았고 두 번 다 미뤄졌다. 2026-08-31 에 화면을 다시 열어 t0 과 t+10초와
              개시가 지난 t+35초 세 번 모두 같은 글자인 것을 확인하고 고친다.

              **고친 방향은 값이 아니라 시제다.** 값을 살아 있는 시계에 묶는 길도 있었고
              배선 비용도 거의 없었다(`nowMs` 가 이미 이 컴포넌트에 있다). 그 길을 안 고른
              이유는 그러면 이 줄이 「내가 마지막에 무엇을 눌렀나」를 더는 말하지 못하기
              때문이다. 0:30 을 적용해도 다음 순간 0:29.5 가 된다. 그 정보를 앞 줄이 대신
              못 한다, 앞 줄은 누를 대상을 말하지 누른 결과를 말하지 않는다. 흐르는 값
              쪽은 정본이 상단 고정줄의 「남은 시간」으로 이미 계약해 두었고 그 자리는
              마지막 스텝이 채운다.

              **행위가 아니라 설정을 말한다.** 처음 고른 문구는 「마지막으로 잡은 값」이었는데
              반증이 그것을 깼다. `sequence confirm` 이 서버에서 손잡이를 30초로 채우므로
              (`src/server/sequence.ts` 의 `DEFAULT_HANDLE_MS`), 조립을 마치고 바로 내보낸
              사람은 **다시 잡기를 한 번도 안 하고** 그 문장을 읽는다. 그것은 이 회차가 없애려던
              것과 같은 종류의 거짓이고, 게다가 가장 흔한 경로다. 「걸려 있는 값」은 누가
              정했든 참이다 (2026-09-01 사용자 결정).

              **그래서 `handleMs` 가 없으면 이 줄을 아예 안 그린다.** 전에는 없을 때 30초를
              대신 그렸다. 값이 없는데 「잡혀 있는 값은 0:30.0」이라고 적으면 걸린 적 없는
              값이 걸려 있다고 말하는 것이다. 내보낸 상태에서 이 갈래는 실제로 안 지나간다,
              손잡이 없이 dispatch 하면 서버가 409 로 막기 때문이다. 그래도 fallback 을
              지우는 이유는 그것이 참인 동안에만 안 보이는 거짓이기 때문이다.

              **`!== null` 이 아니라 `typeof` 로 가르는 이유**는 방송 payload 가 계약
              스키마를 안 지나기 때문이다(`use-sequence.ts` 가 캐스팅한다). 칸이 아예
              빠진 payload 가 오면 `!== null` 은 참이고 `formatTimeText(undefined)` 가
              그려진다. 옛 `??` 는 그 자리도 함께 덮고 있었다. */}
          {typeof props.sequence.handleMs === 'number' && (
            <p data-testid="retime-applied" className="mt-line text-xs text-ink-mute">
              {fill(t.retimeApplied, { value: formatTimeText(props.sequence.handleMs) })}
            </p>
          )}
          <p className="mt-line text-xs text-ink-mute">{t.retimeMarching}</p>
          {/*
            **하한의 이유가 이 자리에도 있어야 한다.** 다시 잡기는 실패 직후 1~2초를 다투며
            더 이른 값을 치는 자리라, time handle 보다 오히려 이유가 더 필요하다. 대리 관측이
            이것을 잡았다 (2026-08-28). 그때 화면은 내보낸 상태였고, 「왜 6초인가」가 time
            handle 바 안에만 있어서 정작 급한 자리에서 안 보였다.

            **문장은 같은 키를 쓴다.** 하한 규칙이 하나인데 문장을 둘로 지으면 반드시 갈리고,
            갈린 뒤에는 어느 쪽이 참인지 화면을 보는 사람이 알 수 없다.
          */}
          <p data-testid="retime-floor-why" className="mt-line text-xs text-ink-mute">
            {t.handleFloorWhy}
          </p>
        </div>

        <button
          type="button"
          data-testid="retime-quick"
          className="btn btn-primary px-5"
          onClick={() => void retime(RETIME_QUICK_MS, text)}
        >
          <Icon name="retime" />
          {t.retimeQuick}
        </button>
      </div>
      <p className="mt-line text-xs text-ink-mute">{t.retimeQuickWhy}</p>

      {notice !== '' && (
        <p data-testid="dispatched-notice" role="status" className="mt-group text-sm text-cyan">
          {notice}
        </p>
      )}
      {message !== '' && (
        <p data-testid="retime-message" role="alert" className="mt-group text-sm text-red-300">
          {message}
        </p>
      )}

      {/* ── 이 작전을 떠나는 두 길 ────────────────────────────────────────────
          화면에 보이는 자리는 재구성 조작 아래, 알림 위다. */}
      <LeaveDispatched
        sequence={props.sequence}
        nowMs={props.nowMs}
        unsavedEdit={props.unsavedEdit}
        onReopen={props.onReopen}
        onDiscard={props.onDiscard}
      />
    </>
  )
}

export function AttackView(): React.ReactElement {
  const t = useStrings()
  const c = useCommon()
  const [ctToken] = useState<string | null>(() => readToken(CT_TOKEN_KEY))
  const store = useSequence(ctToken)

  const [sortAxis, setSortAxis] = useState<SortAxis>('impact')
  const [handleText, setHandleText] = useState('')
  const [handleMessage, setHandleMessage] = useState('')
  const [notice, setNotice] = useState('')
  /** 내보낸 뒤 도구의 알림 한 줄. 줄 수정의 「고쳤습니다」도 여기로 온다. */
  const [toolsNotice, setToolsNotice] = useState('')
  /**
   * 지금 고치는 중인 줄과 묻는 중인 이동. **id 로 들고 있다**, 줄 자체를 들면 방송이 와도 옛
   * 값이 남는다. 전이 규칙은 `edit-switch.ts` 하나가 진다.
   */
  const [edit, setEdit] = useState<EditState>(EDIT_IDLE)
  /** 열린 편집 영역에 저장 안 한 입력이 있는가. 영역이 알려 준다. */
  const [editDirty, setEditDirty] = useState(false)
  /**
   * 행마다의 고치기 버튼. 저장과 취소 뒤 초점을 돌려줄 자리다. 행은 정렬과 방송으로 다시
   * 그려지므로 노드를 하나 쥐지 않고 rally id 로 찾는다.
   */
  const editButtons = useRef(new Map<string, HTMLButtonElement>())
  /** 다음 그림이 끝난 뒤 초점을 돌려줄 행. 닫는 순간에는 버튼이 아직 다시 안 그려졌을 수 있다. */
  const focusBackTo = useRef<string | null>(null)
  const editButtonRef = useCallback((rallyId: string, node: HTMLButtonElement | null): void => {
    if (node === null) editButtons.current.delete(rallyId)
    else editButtons.current.set(rallyId, node)
  }, [])
  useEffect(() => {
    const id = focusBackTo.current
    if (id === null) return
    focusBackTo.current = null
    editButtons.current.get(id)?.focus()
  })
  /** 저장 성공이나 취소로 닫고, 그 행의 고치기 버튼으로 초점을 돌려준다. */
  const closeEditor = useCallback((rallyId: string): void => {
    focusBackTo.current = rallyId
    setEdit(closeEdit())
  }, [])

  // 방금 적재된 줄. **파생시킬 수 없는 유일한 상태다**, 서버 응답만 보면 어느 줄이 새
  // 줄인지 알 수 없다. 적재 전후의 id 집합을 견주어 정한다.
  const [freshId, setFreshId] = useState<string | null>(null)
  const [miniMessage, setMiniMessage] = useState('')
  const [confirmMessage, setConfirmMessage] = useState('')

  const sequence = store.sequence

  /**
   * 이 창이 지금 「이 ops 수정」이나 접기를 보내는 중인가. 내보낸 상태를 벗어난 까닭이 이 창의
   * 조작인지 다른 창의 조작인지를 가른다. 이 창의 조작이면 사람이 경고를 읽고 눌렀으므로 따로
   * 알리지 않는다.
   */
  const leavingHere = useRef(false)
  const sequenceState = sequence?.state ?? null
  /**
   * **내보낸 상태를 벗어나면 줄 수정 상태를 비운다.** 이 창의 되돌리기와 접기는 성공 뒤 스스로
   * 비우지만, 다른 CT 창의 같은 조작은 방송으로만 들어온다. 비우지 않으면 조립 문맥의 목록에
   * 고치는 중 표시가 남고, 다시 dispatch 되는 순간 아무도 누르지 않은 편집 영역이 저절로 열려
   * 화면을 굴리고 초점을 끌어간다(2026-10-14 반증 검토). 그때 저장 안 한 입력이 있었으면 사라진
   * 사실을 알린다. 조용히 사라지게 두지 않는다(사용자 2026-10-14 「조용히 사라지지 않게」).
   */
  useEffect(() => {
    if (sequenceState === 'dispatched' || edit.editingId === null) return
    if (editDirty && !leavingHere.current) setNotice(t.editDroppedRemote)
    setEdit(EDIT_IDLE)
  }, [sequenceState, edit.editingId, editDirty, t])

  /**
   * 그 줄을 맡은 사람의 이름.
   *
   * **못 찾으면 식별자를 그대로 뱉지 않는다.** 내보낸 시퀀스는 행군이 얼어붙어 있어 그
   * 사람이 명부에서 내려가도 줄이 살아남는데, 그때 이름 자리에 UUID 원문이 떴다
   * (2026-08-13 사람 관점 2회차 관측). 사람이 읽는 자리에 내부 식별자가 뜨면 그것은
   * 이름이 아니라 고장으로 읽히고, 무엇보다 **그 줄이 누구 것인지는 여전히 모른다.**
   * 모르면 모른다고 말하는 편이 낫다.
   */
  const nameOf = useCallback(
    (rosterId: string): string =>
      store.allies.find((row) => row.id === rosterId)?.nickname ?? t.rosterGone,
    [store.allies, t.rosterGone],
  )

  /**
   * 새로 생긴 줄을 **그 조작의 응답에서 정한다.**
   *
   * 목록의 전이를 지켜보는 방법도 써 봤는데 그쪽은 **남의 방송에도 반응한다.** 명부에서
   * 사람이 지워져 그 줄이 빠지거나 다른 창의 조작으로 목록이 흔들리는 순간에도 배지가
   * 옮겨 붙었다. 2026-08-13 사람 관점 채점이 그것을 두 번 재현해 보고했다. 배지가 뜻하는
   * 것은 「방금 내가 넣은 줄」이지 「방금 늘어난 줄」이 아니다.
   */
  const addRally = useCallback(
    async (input: Parameters<typeof store.addRally>[0]): Promise<string | null> => {
      const before = new Set((store.sequence?.rallies ?? []).map((line) => line.id))
      const outcome = await store.addRally(input)
      if (outcome.error !== null) return outcome.error
      setMiniMessage('')
      setConfirmMessage('')
      // 응답의 목록에서 이 조작이 만든 줄 하나를 고른다. 둘 이상이면 지목할 근거가 없어
      // 아무것도 가리키지 않는다. 찍어 맞히면 틀린 주장이 된다.
      const added = (outcome.next?.rallies ?? []).filter((line) => !before.has(line.id))
      setFreshId(added.length === 1 ? (added[0]?.id ?? null) : null)
      return null
    },
    [store],
  )

  const removeRally = useCallback(
    async (rallyId: string): Promise<string | null> => {
      const outcome = await store.removeRally(rallyId)
      // 실패 문구는 서버의 것을 그대로 쓴다. 화면이 자기 말로 다시 적으면 막은 이유와
      // 화면이 말하는 이유가 갈린다.
      setMiniMessage(outcome.error ?? '')
      if (outcome.error === null && freshId === rallyId) setFreshId(null)
      return outcome.error
    },
    [freshId, store],
  )

  const confirmSequence = useCallback(async (): Promise<void> => {
    const outcome = await store.confirmSequence()
    setConfirmMessage(outcome.error ?? '')
    // 「방금」은 조립 문맥 안에서만 참이다. 두 탭이던 시절에는 탭을 옮기는 순간 뷰가
    // 언마운트돼 저절로 비워졌는데, 한 뷰에서 세 문맥을 오가는 지금은 여기서 비워야 한다.
    if (outcome.error === null) setFreshId(null)
  }, [store])

  /**
   * 칸의 값이 바뀌면 **그 값에 대해 하던 말을 거둔다.**
   *
   * 거절 문구는 그때 그 값에 대한 판정이라 값이 바뀌면 더 이상 참이 아니다. `0:02` 를
   * 거절한 뒤 한 글자를 지우면 칸에는 `0:0` 이 있는데 붉은 줄은 아직 「(입력값 2초)」라고
   * 적혀 있었다. 화면이 지금 칸에 없는 값을 이름으로 부른다. 명부의 등록 폼과 rally
   * 편집기의 착탄 offset 칸이 이미 같은 규칙을 쓰고 있어서, 한 화면 안에서 입력칸 둘이
   * 서로 다른 규칙으로 살고 있었다. (2026-08-13 사람 관점 채점이 1점으로 값 매겼다.)
   */
  const changeHandleText = useCallback((next: string): void => {
    setHandleText(next)
    setHandleMessage('')
  }, [])

  const applyHandle = useCallback(
    async (millis: number): Promise<void> => {
      const typedWhenSent = handleText
      const outcome = await store.setHandle(millis)
      setHandleMessage(outcome.error ?? '')
      // **적용됐으면 칸을 비운다.** 남겨 두면 칸의 숫자와 바로 아래 「지금부터 … 뒤에
      // 둡니다」가 손가락 한 마디 거리에서 서로 다른 말을 한다. 프리셋을 누른 뒤가 특히
      // 그렇다. 직접 입력에 적어 둔 값은 적용되지 않았는데 칸에만 남는다. 적용된 값의
      // 정본은 그 아래 한 줄이고, 칸은 다음에 넣을 값을 받는 빈 자리다.
      //
      // **다만 그 사이에 사람이 친 글자는 지우지 않는다.** 왕복이 도는 동안 프리셋을
      // 누른 CT 가 곧바로 다음 값을 치면, 뒤늦게 도착한 이 비우기가 그 타이핑을 덮는다.
      // 명부가 같은 경합을 이미 만났다. 보낼 때의 값과 지금 값이 같을 때만 비운다.
      if (outcome.error === null) {
        setHandleText((current) => (current === typedWhenSent ? '' : current))
      }
    },
    [handleText, store],
  )

  const applyTyped = useCallback(async (): Promise<void> => {
    const parsed = parseTimeText(handleText)
    if (!parsed.ok) {
      setHandleMessage(timeParseMessage(parsed.reason, c))
      return
    }
    // **화면이 하한을 스스로 판정하지 않는다.** 서버가 이미 가르고 있고, 두 벌이면 둘이
    // 서로 다르게 낡는다. 경계값 6초가 허용되는 것도 그쪽 한 곳에서만 정해진다.
    // 거절당하면 칸의 값은 남긴다. 고쳐 넣으라는 뜻이라 지우면 다시 쳐야 한다.
    await applyHandle(parsed.millis)
  }, [applyHandle, handleText])

  const dispatch = useCallback(async (): Promise<void> => {
    const outcome = await store.dispatch()
    setHandleMessage(outcome.error ?? '')
  }, [store])

  const discard = useCallback(async (): Promise<string | null> => {
    // 첫 누름은 지우지 않는다. **무엇이 사라지는지 말하고** 한 번 더 누르게 한다. 그 두 번은
    // 버튼이 지고 이 함수는 두 번째 누름만 받는다. 명부 행 삭제와 같은 문법이고, 서버는
    // 두 번 받지 않는다.
    leavingHere.current = true
    const outcome = await store.discard()
    leavingHere.current = false
    setNotice(outcome.error === null ? t.discarded : '')
    setHandleMessage(outcome.error ?? '')
    setHandleText('')
    // 새 시퀀스다. 옛 줄을 가리키던 「방금」과 「고치는 중」을 함께 비운다. 되돌리기와 같은
    // 자리에서 같은 둘을 비워 규칙이 하나가 된다.
    if (outcome.error === null) {
      setFreshId(null)
      setEdit(EDIT_IDLE)
      setToolsNotice('')
    }
    return outcome.error
  }, [store, t])

  /**
   * 편성을 지킨 채 편집으로 되돌린다. **알림이 이 화면에 남아야 한다.**
   *
   * 되돌리는 순간 버튼을 들고 있던 컴포넌트가 통째로 사라지므로(내보낸 상태가 아니게 된다),
   * 알림을 거기 두면 성공했다는 문장이 뜨자마자 함께 사라진다. 접기가 같은 이유로 이 화면에
   * 알림을 둔다.
   */
  const reopen = useCallback(async (): Promise<string | null> => {
    leavingHere.current = true
    const outcome = await store.reopen()
    leavingHere.current = false
    setNotice(outcome.error === null ? t.reopened : '')
    setHandleMessage(outcome.error ?? '')
    // 고치는 중이던 줄을 잊는다. 남겨 두면 다음 dispatch 순간 아무도 누르지 않은 줄 수정
    // 패널이 저절로 열린다. 두 탭이던 시절에는 탭 전환의 언마운트가 이것을 대신 비웠다.
    if (outcome.error === null) {
      setEdit(EDIT_IDLE)
      setFreshId(null)
      setToolsNotice('')
    }
    return outcome.error
  }, [store, t])

  if (!store.loaded || sequence === null) {
    return (
      <section data-testid="attack-view" className="py-group">
        <p className="text-sm text-ink-mute">{t.rallyTitle}</p>
      </section>
    )
  }

  const lines = sequence.rallies.length
  const hasMain = sequence.rallies.some((line) => line.type === 'main')
  const dispatched = sequence.state === 'dispatched'
  const confirmed = sequence.state === 'confirmed'
  // 고르던 줄이 사라지면(다른 창에서 접었거나 그 사람이 명부에서 내려갔거나) 패널도 함께
  // 닫힌다. 상태에서 파생시키므로 「없는 줄을 고치는 중」이라는 상태가 만들어질 수 없다.
  const editing = sequence.rallies.find((line) => line.id === edit.editingId) ?? null
  // 고르던 줄이 사라졌으면 상태도 쉬는 것으로 읽는다. 남은 id 와 「저장 안 한 입력 있음」으로
  // 물으면 화면에 없는 영역이 다음 고치기를 가로막는다. 내보낸 상태가 아니어도 쉰다. 다른 창의
  // 되돌리기가 방송으로 들어온 그 렌더에서 옛 상태가 한 번이라도 그려지지 않게 한다(위의 effect 가
  // 상태 자체를 비우기 전).
  const liveEdit = editing === null || !dispatched ? EDIT_IDLE : edit
  const pendingMove = liveEdit.pending
  const pendingTo =
    pendingMove?.kind === 'switch'
      ? (sequence.rallies.find((line) => line.id === pendingMove.to) ?? null)
      : null

  // **목록은 세 문맥에서 같은 것 하나다.** 정렬과 fresh 표시와 줄 오른쪽 조작을 목록이
  // 문맥에 맞게 스스로 가른다. 여기서는 어느 갈래에 들어가든 같은 요소를 넘긴다.
  const renderList = (editor: React.ReactNode): React.ReactElement => (
    <RallyMiniSequence
      sequence={sequence}
      nameOf={nameOf}
      freshId={freshId}
      onRemove={removeRally}
      message={miniMessage}
      sortAxis={sortAxis}
      onSort={setSortAxis}
      editingId={liveEdit.editingId}
      onToggleEdit={(rallyId) => {
        setEdit(requestEdit(liveEdit, rallyId, editing !== null && editDirty))
      }}
      editor={editor}
      editButtonRef={editButtonRef}
    />
  )

  return (
    <section data-testid="attack-view" className="py-group" aria-label={t.rallyTitle}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="section-title flex items-center gap-2.5">
          <IconTile name="attack" tone="accent" size="sm" />
          {t.rallyTitle}
        </h2>
        {dispatched ? (
          <span
            data-testid="dispatch-badge"
            className="chip chip-cyan"
          >
            {t.dispatchBadge}
          </span>
        ) : (
          <span data-testid="not-fixed-yet" className="text-xs text-ink-mute">
            {t.notFixedYet}
          </span>
        )}
      </div>
      {dispatched && <p className="mt-line text-xs text-ink-mute">{t.dispatchBadgeWhy}</p>}

      {/* **머리글이 문맥을 따라간다.** 조립 시절 문장을 그대로 두었더니 counter rally
          문맥에서 「여기까지는 절대 시각이 없습니다」가 거짓이 됐다. 진행 중 시퀀스는 착탄
          원점이 이미 얼어붙어 **적재하는 순간 그 줄의 착탄 시각이 정해진다.** 확정된 것을
          미확정이라 부르는 문장이었다. (2026-08-15 대리 관측이 1점으로 값 매겼다.) */}
      <p data-testid="rally-why" className="mt-line text-xs text-ink-mute">
        {dispatched ? t.rallyWhyCounter : t.rallyWhy}
      </p>

      {/* **다음 할 일 하나.** 이 화면에서 지금 무엇을 하면 되는지를 한 줄로 말한다. */}
      <NextStep
        text={
          store.allies.length === 0
            ? t.nextSeedRoster
            : dispatched
              ? t.nextRetime
              : confirmed
                ? sequence.handleMs === null
                  ? t.nextSetHandle
                  : t.nextDispatch
                : hasMain
                  ? t.nextAddSub
                  : t.nextAddMain
        }
      />

      {dispatched ? (
        <DispatchedClock>
          {(nowMs) => (
            <>
              <div className="mt-block grid min-w-0 gap-6 md:grid-cols-2">
                <CounterRallyPane
                  allies={store.allies}
                  sequence={sequence}
                  nowMs={nowMs}
                  onAdd={addRally}
                />
                {renderList(
                  editing === null ? null : (
                    // `key` 로 줄마다 새로 마운트한다. 상태를 이어 쓰면 다른 줄을 고르는 순간 앞
                    // 줄의 간격과 rallying 이 새 줄의 칸에 남고, 그 값은 이 줄의 것이 아니다.
                    <RallyEditInline
                      key={editing.id}
                      rally={editing}
                      allies={store.allies}
                      live={{ impactMainAt: sequence.impactMainAt ?? 0, nowMs }}
                      nameOf={nameOf}
                      onDirtyChange={setEditDirty}
                      pending={liveEdit.pending}
                      pendingName={pendingTo === null ? '' : nameOf(pendingTo.rosterId)}
                      onConfirmPending={() => {
                        // 닫기로 끝나면 그 행의 버튼으로 초점을 돌려준다. 다른 행으로 옮기면 새
                        // 영역이 자기 첫 입력칸으로 초점을 가져간다.
                        if (liveEdit.pending?.kind === 'close') focusBackTo.current = editing.id
                        setEdit(confirmPending(liveEdit))
                      }}
                      onKeepEditing={() => {
                        setEdit(keepEditing(liveEdit))
                      }}
                      onCancel={() => {
                        closeEditor(editing.id)
                      }}
                      onApply={async (input) => {
                        const failed = (await store.editRally(input)).error
                        if (failed === null) {
                          setToolsNotice(t.edited)
                          closeEditor(input.rallyId)
                        }
                        return failed
                      }}
                    />
                  ),
                )}
              </div>

              {/* ── 내보낸 뒤의 조작들, 다시 잡기와 떠나는 두 길 ──────────────── */}
              <DispatchedTools
                sequence={sequence}
                nowMs={nowMs}
                notice={toolsNotice}
                onNotice={setToolsNotice}
                unsavedEdit={liveEdit.editingId !== null && editDirty}
                onRetime={async (handleMs) => (await store.retime(handleMs)).error}
                onReopen={reopen}
                onDiscard={discard}
              />
            </>
          )}
        </DispatchedClock>
      ) : (
        <>
          <div className="mt-block grid min-w-0 gap-6 md:grid-cols-2">
            <RallyFormAttack allies={store.allies} sequence={sequence} onAdd={addRally} />
            {renderList(null)}
          </div>

          {/* ── 조립을 마친다 ──────────────────────────────────────────────
              **전환 전에는 손잡이도 내보내기도 DOM 에 없다.** 숨김이나 비활성으로 두면
              「노출되지 않는다」의 판정이 흐려지고, 흐려진 판정은 채점자의 재량이 된다.

              **마친 뒤에는 그 버튼도 없다.** 남겨 두면 방금 끝낸 단계를 다시 하자고 권하는
              큰 버튼이 그 자리에 남고, 그 옆의 설명은 「그 전에는 손잡이도 없습니다」라며
              이미 지나간 시절을 가리킨다. 눌러도 서버가 409 로 거절하므로 **눌러도 되는
              것처럼 생긴 거절되는 문**이 된다. 이 화면의 다른 조작은 전부 누른 자리에서
              답하는데 이것만 침묵했다. (2026-08-13 사람 관점 채점이 1점으로 값 매겼다.) */}
          <div className="mt-block">
            {confirmed ? (
              <div data-testid="sequence-confirmed">
                <p className="text-sm text-cyan">{t.sequenceConfirmed}</p>
              </div>
            ) : (
              <>
                <button
                  type="button"
                  data-testid="sequence-confirm"
                  disabled={!hasMain}
                  className="btn btn-primary"
                  onClick={() => void confirmSequence()}
                >
                  <Icon name="check" />
                  {t.sequenceConfirm}
                </button>
                <p className="mt-line text-xs text-ink-mute">{t.sequenceConfirmWhy}</p>
                {/* 거절 문구도 조립 문맥의 것이다. 문맥이 바뀌면 함께 사라진다. */}
                {confirmMessage !== '' && (
                  <p
                    data-testid="sequence-confirm-message"
                    role="alert"
                    className="mt-line text-sm text-red-300"
                  >
                    {confirmMessage}
                  </p>
                )}
              </>
            )}
          </div>

          {/* ── 하단 고정 바, **마친 뒤에만 존재한다** ─────────────────────── */}
          {confirmed && (
            <div
              data-testid="handle-bar"
              className="dock-bar sticky bottom-0 mt-block flex flex-wrap items-end gap-2 py-group"
            >
              <div className="min-w-0 flex-1">
                {/* c2 장식: 아래 고정 바도 패널 머리와 같은 문법(아이콘 타일 + 제목)으로 시작한다(SaaS 비평: 이 바만 새 체계를 안 받았다). */}
                <p className="section-title flex items-center gap-2">
                  <IconTile name="clock" tone="accent" size="sm" />
                  {/* 라벨(time handle)과 뜻풀이 두 요소. 다시 잡기 바와 같은 모양으로 맞춘다(2026-10-14, 세션 선택: 콜론으로 잇던 자리도 함께 나눔). */}
                  <LabelHint label={t.handleTitle} hint={t.handleTitleHint} />
                </p>
                <div className="mt-line flex flex-wrap items-end gap-2">
                  {HANDLE_PRESETS.map((preset) => (
                    <button
                      key={preset}
                      type="button"
                      data-testid={`handle-preset-${String(preset / 1000)}`}
                      data-active={String(sequence.handleMs === preset)}
                      className={`seg ${sequence.handleMs === preset ? 'is-on' : ''}`}
                      onClick={() => void applyHandle(preset)}
                    >
                      {fill(t.handlePreset, { value: String(preset / 1000) })}
                    </button>
                  ))}
                  <div className="w-32">
                    <TimeInput
                      testId="handle-input"
                      label={t.handleCustom}
                      value={handleText}
                      onChange={changeHandleText}
                      compact
                    />
                  </div>
                  <button
                    type="button"
                    data-testid="handle-apply"
                    className="btn btn-secondary"
                    onClick={() => void applyTyped()}
                  >
                    {t.handleApply}
                  </button>
                </div>
                {sequence.handleMs !== null && (
                  <p data-testid="handle-applied" className="mt-line text-xs text-ink-mute">
                    {fill(t.handleApplied, { value: formatTimeText(sequence.handleMs) })}
                  </p>
                )}
                {/*
                  **하한의 이유는 늘 떠 있다.** 예전에는 손잡이를 아직 안 놓았을 때만 떴고,
                  한 번 놓으면 값 문장으로 바뀌어 사라졌다. 그런데 하한을 어기는 사람은 값을
                  놓아 본 뒤에 더 이른 값을 다시 치는 사람이라, 이유가 필요한 바로 그 순간에
                  이유가 없었다.

                  **화면이 하한을 미리 판정하게 만들지 않는 이유는 아래 `applyTyped` 에 있다.**
                  설명을 늘 보이게 두는 것과 판정을 두 벌로 만드는 것은 다른 일이고, 여기서
                  얻으려는 것은 앞의 것뿐이다. 손잡이가 없을 때 무엇을 하라는 안내는 이 화면
                  맨 위의 「다음 할 일」이 이미 지고 있어서, 이 자리는 규칙만 말하면 된다.
                */}
                <p data-testid="handle-floor-why" className="mt-line text-xs text-ink-mute">
                  {t.handleFloorWhy}
                </p>
              </div>

              <button
                type="button"
                data-testid="dispatch"
                disabled={lines === 0 || sequence.handleMs === null}
                className="btn btn-primary px-5"
                onClick={() => void dispatch()}
              >
                <Icon name="send" />
                {t.dispatch}
              </button>
            </div>
          )}
          {confirmed && <p className="mt-line text-xs text-ink-mute">{t.dispatchWhy}</p>}
        </>
      )}

      {notice !== '' && (
        <p data-testid="attack-notice" role="status" className="mt-group text-sm text-cyan">
          {notice}
        </p>
      )}
      {handleMessage !== '' && (
        <p data-testid="handle-message" role="alert" className="mt-group text-sm text-red-300">
          {handleMessage}
        </p>
      )}
    </section>
  )
}
